import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Calendar, CreditCard, Euro, Plus, Receipt, RefreshCw, Shield, Wallet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { DatePickerFr } from '../ui/DatePickerFr';
import { BACKEND_URL, MODES_PAIEMENT, MODALITES, DEFAULT_CONTRIBUTIONS } from './constants';
import { getToken } from '../../services/tokenService';
import ContributionsEditor from './ContributionsEditor';

export default function RecuForm({ mode, selectedSub, onGenerate, generating }) {
  const [f, setF] = useState({
    raison_sociale: '', siret: '', adresse: '', code_postal: '', ville: '',
    ref_solidaire: '', numero_adherent: '', objet: 'Contribution Solidaire ESS',
    montant_ht: '', montant_ttc: '', taux_tva: '0', montant_tva: '0',
    type_dispositif: 'COPPAM',
    sous_type_dispositif: '',
    date_emission: new Date().toISOString().split('T')[0],
    mode_paiement: 'virement', modalite_paiement: '', reference_paiement: '',
    montant_acompte: '', echeancier_details: '', observations: '',
  });

  const [contributions, setContributions] = useState(DEFAULT_CONTRIBUTIONS);
  const [selectedContribs, setSelectedContribs] = useState(new Set(DEFAULT_CONTRIBUTIONS.map(c => c.id)));
  const [showCustomDispositif, setShowCustomDispositif] = useState(false);
  const [customDispositif, setCustomDispositif] = useState('');
  const [sousTypes, setSousTypes] = useState([]);
  const [loadingSousTypes, setLoadingSousTypes] = useState(false);
  const headers = { Authorization: `Bearer ${getToken()}` };

  const [modele, setModele] = useState('general');
  useEffect(() => {
    const appliquer = (taxes) => {
      setContributions(taxes);
      setSelectedContribs(new Set(taxes.map(t => t.id)));
    };
    const disp = encodeURIComponent(f.type_dispositif || '');
    const loadContributions = async () => {
      try {
        const cfg = await axios.get(`${BACKEND_URL}/api/recus-ess/contributions-config?dispositif=${disp}`, { headers });
        setModele(cfg.data?.modele || 'general');
        if (cfg.data?.contributions?.length) { appliquer(cfg.data.contributions); return; }
      } catch { /* repli sur la configuration BATICO */ }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/batico/config/v3/taxes/annuel`, { headers });
        const taxes = res.data?.taxes?.filter(t => t.actif && !t.archived) || [];
        if (taxes.length > 0) appliquer(taxes.map(t => ({ id: t.id, nom: t.nom, taux: t.taux })));
      } catch {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/batico/config/v2/taxes/annuel`, { headers });
          const taxes = res.data?.taxes?.filter(t => t.actif && !t.archived) || [];
          if (taxes.length > 0) appliquer(taxes.map(t => ({ id: t.id, nom: t.nom, taux: t.taux })));
        } catch { /* fallback to defaults */ }
      }
    };
    const t = setTimeout(loadContributions, showCustomDispositif ? 600 : 0);
    return () => clearTimeout(t);
  }, [f.type_dispositif]);

  // Load sous-types when dispositif changes
  useEffect(() => {
    const loadSousTypes = async () => {
      const disp = f.type_dispositif;
      if (!disp || showCustomDispositif) { setSousTypes([]); return; }
      setLoadingSousTypes(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/dispositifs-config/${encodeURIComponent(disp)}/sous-types`, { headers });
        setSousTypes(res.data.sous_types || []);
      } catch { setSousTypes([]); }
      finally { setLoadingSousTypes(false); }
    };
    loadSousTypes();
  }, [f.type_dispositif, showCustomDispositif]);

  useEffect(() => {
    if (mode === 'lie' && selectedSub) {
      setF(prev => ({
        ...prev,
        raison_sociale: selectedSub.raison_sociale || '',
        siret: selectedSub.siret || '',
        adresse: selectedSub.adresse || '',
        code_postal: selectedSub.code_postal || '',
        ville: selectedSub.ville || '',
        ref_solidaire: selectedSub.ref_solidaire || '',
        numero_adherent: selectedSub.adherent_id || '',
        montant_ht: selectedSub.montant_ttc > 0 ? String(selectedSub.montant_ttc) : '',
      }));
    }
  }, [mode, selectedSub]);

  const totalContribRate = contributions.filter(c => selectedContribs.has(c.id)).reduce((sum, c) => sum + (parseFloat(c.taux) || 0), 0);

  const recalcMontants = useCallback((base, rate) => {
    const baseVal = parseFloat(base) || 0;
    const contribAmount = baseVal * rate / 100;
    const global = baseVal + contribAmount;
    return { montant_ht: base, montant_ttc: global.toFixed(2), taux_tva: String(rate), montant_tva: contribAmount.toFixed(2) };
  }, []);

  const handleBaseChange = (val) => {
    setF(p => ({ ...p, ...recalcMontants(val, totalContribRate) }));
  };

  const toggleContrib = (id) => {
    setSelectedContribs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllContribs = () => {
    const allSelected = contributions.every(c => selectedContribs.has(c.id));
    setSelectedContribs(allSelected ? new Set() : new Set(contributions.map(c => c.id)));
  };

  const changeContributions = (liste, nouvelId) => {
    setContributions(liste);
    if (nouvelId) setSelectedContribs(prev => new Set([...prev, nouvelId]));
  };

  useEffect(() => {
    setF(p => (p.montant_ht ? { ...p, ...recalcMontants(p.montant_ht, totalContribRate) } : p));
  }, [totalContribRate, recalcMontants]);

  const handleSubmit = async () => {
    if ((mode === 'libre' || mode === 'non_ref') && !f.raison_sociale.trim()) { toast.error('La raison sociale est obligatoire'); return; }
    if (mode === 'lie' && !selectedSub) { toast.error('Selectionnez une souscription'); return; }
    if (!f.montant_ht || parseFloat(f.montant_ht) <= 0) { toast.error('Le Montant de base est obligatoire'); return; }
    if (!f.date_emission) { toast.error("La date d'emission est obligatoire"); return; }
    if (contributions.some(c => !String(c.nom || '').trim())) { toast.error('Chaque contribution doit avoir un libellé'); return; }
    const base = parseFloat(f.montant_ht) || 0;
    const retenues = contributions.filter(c => selectedContribs.has(c.id)).map(c => ({
      nom: c.nom.trim(), taux: parseFloat(c.taux) || 0, montant: Math.round(base * (parseFloat(c.taux) || 0)) / 100,
    }));
    try {
      await axios.put(`${BACKEND_URL}/api/recus-ess/contributions-config?dispositif=${encodeURIComponent(f.type_dispositif || '')}`,
        { contributions: contributions.map(c => ({ id: c.id, nom: c.nom, taux: parseFloat(c.taux) || 0 })) }, { headers });
    } catch { toast.error('Libellés et taux non enregistrés pour les reçus suivants'); }
    onGenerate({ ...f, contributions: retenues }, selectedSub);
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500";
  const labelCls = "block text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="space-y-5">
      {(mode === 'libre' || mode === 'non_ref') && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-orange-500" /> Beneficiaire</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className={labelCls}>Raison sociale *</label><input type="text" value={f.raison_sociale} onChange={e => setF(p => ({ ...p, raison_sociale: e.target.value }))} className={inputCls} data-testid="recu-raison-sociale" /></div>
            <div><label className={labelCls}>SIRET</label><input type="text" value={f.siret} onChange={e => setF(p => ({ ...p, siret: e.target.value }))} className={inputCls} data-testid="recu-siret" /></div>
            <div><label className={labelCls}>Ref. Solidaire</label><input type="text" value={f.ref_solidaire} onChange={e => setF(p => ({ ...p, ref_solidaire: e.target.value }))} className={inputCls} data-testid="recu-ref-solidaire" /></div>
            <div><label className={labelCls}>Adresse</label><input type="text" value={f.adresse} onChange={e => setF(p => ({ ...p, adresse: e.target.value }))} className={inputCls} /></div>
            <div><label className={labelCls}>Code postal</label><input type="text" value={f.code_postal} onChange={e => setF(p => ({ ...p, code_postal: e.target.value }))} className={inputCls} /></div>
            <div><label className={labelCls}>Ville</label><input type="text" value={f.ville} onChange={e => setF(p => ({ ...p, ville: e.target.value }))} className={inputCls} /></div>
            <div><label className={labelCls}>N Adherent</label><input type="text" value={f.numero_adherent} onChange={e => setF(p => ({ ...p, numero_adherent: e.target.value }))} className={inputCls} /></div>
            <div className="md:col-span-2"><label className={labelCls}>Objet</label><input type="text" value={f.objet} onChange={e => setF(p => ({ ...p, objet: e.target.value }))} className={inputCls} /></div>
          </div>
        </div>
      )}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Shield className="h-4 w-4 text-orange-500" /> Type de Dispositif</h4>
        <div className="flex flex-wrap gap-2">
          {['COPPAM', 'FTPE', 'F.O.G.E.D.O.M', 'FOGEDOM-SCIC'].map(d => (
            <button key={d} type="button" onClick={() => { setF(p => ({ ...p, type_dispositif: d })); setShowCustomDispositif(false); }}
              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${f.type_dispositif === d && !showCustomDispositif ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-200 hover:border-gray-300 text-gray-500'}`}
              data-testid={`recu-dispositif-${d.toLowerCase().replace(/\./g, '')}`}>
              {d}
            </button>
          ))}
          <button type="button" onClick={() => { setShowCustomDispositif(true); setF(p => ({ ...p, type_dispositif: customDispositif })); }}
            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-1.5 ${showCustomDispositif ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-dashed border-gray-300 hover:border-gray-400 text-gray-500'}`}
            data-testid="recu-dispositif-custom">
            <Plus className="h-3.5 w-3.5" /> Ajouter
          </button>
        </div>
        {showCustomDispositif && (
          <div className="mt-2">
            <input type="text" value={customDispositif} onChange={e => { setCustomDispositif(e.target.value); setF(p => ({ ...p, type_dispositif: e.target.value })); }}
              placeholder="Saisir un type de dispositif personnalise" className={inputCls} autoFocus data-testid="recu-dispositif-custom-input" />
          </div>
        )}
        {/* Sous-type de dispositif */}
        {!showCustomDispositif && sousTypes.length > 0 && (
          <div className="mt-3" data-testid="sous-type-selector">
            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Sous-type de {f.type_dispositif}</label>
            <div className="flex flex-wrap gap-1.5">
              <button type="button"
                onClick={() => setF(p => ({ ...p, sous_type_dispositif: '' }))}
                className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${!f.sous_type_dispositif ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-gray-300 text-gray-400'}`}
                data-testid="sous-type-none">
                Aucun
              </button>
              {sousTypes.map(st => (
                <button key={st.id} type="button"
                  onClick={() => setF(p => ({ ...p, sous_type_dispositif: st.nom }))}
                  className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${f.sous_type_dispositif === st.nom ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-200 hover:border-gray-300 text-gray-500'}`}
                  data-testid={`sous-type-${st.id}`}>
                  {st.nom}
                </button>
              ))}
            </div>
          </div>
        )}
        {!showCustomDispositif && loadingSousTypes && (
          <div className="mt-2 text-xs text-gray-400 flex items-center gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> Chargement sous-types...</div>
        )}
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Euro className="h-4 w-4 text-orange-500" /> Montants</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><label className={labelCls}>Montant de base *</label>
            <div className="relative"><input type="number" step="0.01" min="0" value={f.montant_ht} onChange={e => handleBaseChange(e.target.value)} className={`${inputCls} pr-12`} data-testid="recu-montant-base" /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">EUR</span></div>
          </div>
          <div className="md:col-span-2">
            <ContributionsEditor contributions={contributions} selected={selectedContribs} base={f.montant_ht}
              onChange={changeContributions} onToggle={toggleContrib} onToggleAll={toggleAllContribs}
              modele={modele === 'general' ? `général (aucun modèle ${f.type_dispositif || ''} encore enregistré)` : f.type_dispositif} />
          </div>
          <div><label className={labelCls}>Montant Global</label><input readOnly value={parseFloat(f.montant_ttc) > 0 ? `${parseFloat(f.montant_ttc).toFixed(2)} EUR` : '-'} className={`${inputCls} bg-orange-50 font-semibold text-orange-800 border-orange-200`} data-testid="recu-montant-global" /></div>
        </div>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Calendar className="h-4 w-4 text-orange-500" /> Date et reference</h4>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Date d'emission *</label><DatePickerFr value={f.date_emission} onChange={v => setF(p => ({ ...p, date_emission: v }))} className="w-full" id="recu-date-emission" /></div>
          <div><label className={labelCls}>Reference paiement</label><input type="text" value={f.reference_paiement} onChange={e => setF(p => ({ ...p, reference_paiement: e.target.value }))} placeholder="Ex: VIR-2025-001" className={inputCls} data-testid="recu-reference" /></div>
        </div>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><CreditCard className="h-4 w-4 text-orange-500" /> Mode de paiement</h4>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
          {MODES_PAIEMENT.map(m => {
            const Icon = m.icon; const active = f.mode_paiement === m.value;
            return (
              <button key={m.value} type="button" onClick={() => setF(p => ({ ...p, mode_paiement: m.value }))}
                className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all text-center ${active ? 'border-orange-400 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-200 hover:border-gray-300 text-gray-500'}`} data-testid={`recu-mode-${m.value}`}>
                <Icon className={`h-4 w-4 ${active ? 'text-orange-600' : 'text-gray-400'}`} />
                <span className="text-[10px] font-medium leading-tight">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Wallet className="h-4 w-4 text-orange-500" /> Modalites</h4>
        <select value={f.modalite_paiement} onChange={e => setF(p => ({ ...p, modalite_paiement: e.target.value }))} className={`${inputCls} mb-2`} data-testid="recu-modalite">
          {MODALITES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        {f.modalite_paiement === 'acompte' && (
          <div className="mt-2"><label className={labelCls}>Montant de l'acompte (EUR)</label><input type="number" step="0.01" min="0" value={f.montant_acompte} onChange={e => setF(p => ({ ...p, montant_acompte: e.target.value }))} className={inputCls} data-testid="recu-acompte" /></div>
        )}
        {f.modalite_paiement === 'echeancier' && (
          <div className="mt-2"><label className={labelCls}>Details echeancier</label><textarea rows={2} value={f.echeancier_details} onChange={e => setF(p => ({ ...p, echeancier_details: e.target.value }))} className={inputCls} data-testid="recu-echeancier" /></div>
        )}
      </div>
      <div><label className={labelCls}>Observations</label><textarea rows={2} value={f.observations} onChange={e => setF(p => ({ ...p, observations: e.target.value }))} placeholder="Notes complementaires" className={inputCls} data-testid="recu-observations" /></div>
      <div className="flex gap-3 justify-end pt-2">
        <Button onClick={handleSubmit} disabled={generating} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg px-6" data-testid="recu-generate-btn">
          {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
          Generer le Recu ESS
        </Button>
      </div>
      <div className="border-2 border-emerald-600 rounded-lg p-4 bg-emerald-50/50 mt-4" data-testid="mentions-legales-ess">
        <h4 className="font-bold text-emerald-800 text-sm mb-3 uppercase tracking-wide">MENTIONS LEGALES ESS</h4>
        <div className="border border-emerald-300 rounded-md p-3 bg-white/60 mb-3">
          <p className="font-bold text-emerald-700 text-xs mb-1.5">Qualification juridique – ESS</p>
          <p className="text-xs text-gray-700 leading-relaxed">Le present recu est emis par OSO en qualite de structure d'hebergement administratif et comptable. Il ne correspond ni a la vente d'un bien, ni a la fourniture d'une prestation commerciale, ni a une prime ou cotisation d'assurance. Le versement constitue une participation solidaire ESS, sans transfert de risque, sans droit automatique a indemnisation, et hors champ du Code des assurances (jurisprudence CE 10 fevrier 1984 – doctrine ACPR 2019 / 2022).</p>
        </div>
        <p className="text-xs text-gray-800 font-bold mb-2">Ce recu n'a pas valeur de recu fiscal.</p>
        <p className="text-xs text-gray-700 leading-relaxed"><strong>O'SCOP agit en qualite d'operateur administratif et financier du dispositif solidaire.</strong><br />Le present recu atteste exclusivement du versement effectue par le membre au titre d'une contribution solidaire ESS. Il ne constitue ni une facture commerciale, ni une prime d'assurance, ni une garantie automatique, et ne releve pas du Code des assurances ni de la competence de l'ACPR.</p>
      </div>
    </div>
  );
}

